import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Inject,
  Input,
  OnInit,
  Self,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl, UntypedFormControl } from '@angular/forms';
import { DatepickerRangeModel, DatepickerRangeOutput } from '../../common/models';
import { AutoUnsubscribe } from '../../common/decorators';
import {
  LOCALE_CODE_LIST,
  LOCALE_SHORT_DISPLAY_DATE_FORMAT,
  MODIFIED_DATE_TIMEPICKER_CONFIG,
  TIME_12HOUR_FORMAT,
  TIME_SHORT_FORMAT,
} from '../../common/constants/date-config';
import { Subscription } from 'rxjs';
import {
  DateTimeAdapter,
  OWL_DATE_TIME_FORMATS,
  OwlDateTimeComponent,
  OwlDateTimeFormats,
  PickerType,
} from '@danielmoncada/angular-datetime-picker';
import { filter, take } from 'rxjs/operators';
import { AppFacade, AuthFacade } from '../../store';
import { endOfDay, getHours, getMinutes, startOfDay } from 'date-fns';
import { IconNames } from '../../common/constants/icon-names';
import { getDateAsUTC, getTimezoneOffsetInMilliseconds } from '../../common';
import { DateModule } from '../../date.module';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import moment from 'moment';

@Component({
  selector: 'ui-date-time-range',
  imports: [DateModule, SvgIconComponent],
  templateUrl: './date-time-range.component.html',
  styleUrls: ['./date-time-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: OWL_DATE_TIME_FORMATS,
      useFactory: (): OwlDateTimeFormats => ({ ...MODIFIED_DATE_TIMEPICKER_CONFIG }),
    },
  ],
})
@AutoUnsubscribe
export class DateTimeRangeComponent implements OnInit, ControlValueAccessor {
  @Input()
  label: string;

  @Input() pickerType: PickerType = 'both';
  @Input() initialValue: DatepickerRangeModel = new DatepickerRangeModel();
  @Input() removeBottomPadding?: boolean = false;
  @Input() timezone: string | null = null;

  @ViewChild('datetimePicker') datetimePicker: OwlDateTimeComponent<unknown>;

  @ContentChild('prefix') prefix: TemplateRef<HTMLElement>;
  @ContentChild('suffix') suffix: TemplateRef<HTMLElement>;

  iconNames = IconNames;
  dateTimeRangeControl = new UntypedFormControl();

  private subscriptions: Subscription[] = [];

  constructor(
    @Self() public ngControl: NgControl,
    public dateTimeAdapter: DateTimeAdapter<Date>,
    private appFacade: AppFacade,
    @Inject(OWL_DATE_TIME_FORMATS) private owlDateTimeFormats: OwlDateTimeFormats,
    private authFacade: AuthFacade,
  ) {
    ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.initDateTimeRangeValueSubscription();
    const langSubscription = this.appFacade.currentLanguage$.pipe(filter(Boolean)).subscribe((data: string) => {
      const locale = LOCALE_CODE_LIST[data];
      this.dateTimeAdapter.setLocale(locale);
      this.dateTimeAdapter.firstDayOfTheWeek = moment.localeData(locale).firstDayOfWeek();
    });
    this.subscriptions.push(
      this.authFacade.isTwentyFourHourTime$.pipe(take(1)).subscribe((is24h: boolean) => {
        this.owlDateTimeFormats.fullPickerInput = is24h
          ? `${LOCALE_SHORT_DISPLAY_DATE_FORMAT} ${TIME_SHORT_FORMAT}`
          : `${LOCALE_SHORT_DISPLAY_DATE_FORMAT} ${TIME_12HOUR_FORMAT}`;
      }),
      langSubscription,
      this.dateTimeRangeControl.valueChanges
        .pipe(filter(() => this.pickerType === 'calendar'))
        .subscribe((data: DatepickerRangeOutput) => {
          this.onChange({
            start: startOfDay(new Date(data[0])),
            end: endOfDay(new Date(data[1])),
          });
        }),
    );
  }

  registerOnChange(fn: () => DatepickerRangeOutput): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(dateTimeRange: DatepickerRangeOutput): void {
    const startDateForPicker = dateTimeRange?.start ? dateTimeRange?.start : null;
    const endDateForPicker = dateTimeRange?.end ? dateTimeRange?.end : null;

    this.dateTimeRangeControl.setValue([startDateForPicker, endDateForPicker], { emitEvent: false });
  }

  private onChange: (value: DatepickerRangeOutput) => DatepickerRangeOutput;

  private onTouched: () => void;

  private initDateTimeRangeValueSubscription(): void {
    const subscription = this.dateTimeRangeControl.valueChanges
      .pipe(filter(() => this.pickerType !== 'calendar'))
      .subscribe((dateTimeRange: [Date, Date]) => {
        const datePickerRange = new DatepickerRangeOutput();

        if (getHours(dateTimeRange[1]) === 0 && getMinutes(dateTimeRange[1]) === 0) {
          dateTimeRange[1] = endOfDay(dateTimeRange[1]);
        }

        datePickerRange.start = this.toUtcEpoch(new Date(dateTimeRange[0]));
        datePickerRange.end = this.toUtcEpoch(new Date(dateTimeRange[1]));
        this.onChange(datePickerRange);
      });

    this.subscriptions.push(subscription);
  }

  resetDatepicker(): void {
    this.writeValue(new DatepickerRangeOutput(this.initialValue));
    this.onChange(new DatepickerRangeOutput(this.initialValue));
  }

  private toUtcEpoch(date: Date): Date {
    if (this.timezone) {
      return new Date(date.valueOf() - getTimezoneOffsetInMilliseconds(this.timezone));
    }
    return getDateAsUTC(date);
  }
}
